import * as React from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { getFontUrl } from '../../utils/fontUrls';
import type { CrossingSize } from '../../Reducers/Configurator/fontData';

const MODAL_FONT_FAMILY = 'WehaCrossingSizeModalFont';

function crossingSizeModalFontCss(fontUrl: string): string {
  return `@font-face { font-family: '${MODAL_FONT_FAMILY}'; src: url('${fontUrl}') format('truetype'); }`;
}

export interface CrossingSizeOption extends CrossingSize {
  height: number;
  width: number;
}

/** Format size as "H 14,0 | B 7,0 cm" (one decimal, comma separator). */
export function formatCrossingSizeLabel(size: { height: number; width: number }): string {
  const h = size.height.toFixed(1).replace('.', ',');
  const b = size.width.toFixed(1).replace('.', ',');
  return `H ${h} | B ${b} cm`;
}

function crossingSizeEquals(
  a: { height: number; width: number } | null | undefined,
  b: { height: number; width: number } | null | undefined
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a.height === b.height && a.width === b.width;
}

interface SelectCrossingSizeDialogProps {
  open: boolean;
  onClose: () => void;
  crossingSizes: CrossingSizeOption[];
  selectedCrossingSize: { height: number; width: number } | null | undefined;
  onSelect: (size: { height: number; width: number } | null) => void;
  fontFamily: string;
  /** Character used to render the support cross (e.g. "+"). */
  supportCrossCharacter?: string;
  /** Notified when the dialog opens or closes (e.g. so the canvas can hide Moveable). */
  onOpenChange?: (open: boolean) => void;
}

export default function SelectCrossingSizeDialog({
  open,
  onClose,
  crossingSizes,
  selectedCrossingSize,
  onSelect,
  fontFamily,
  supportCrossCharacter = '+',
  onOpenChange,
}: SelectCrossingSizeDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const w = (theme as { weha?: Record<string, unknown> }).weha || {};
  const fontUrl = getFontUrl(fontFamily);
  const [localSelection, setLocalSelection] = React.useState<{ height: number; width: number } | null>(null);

  React.useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  React.useEffect(() => {
    if (open) {
      setLocalSelection(selectedCrossingSize ?? null);
    }
  }, [open, selectedCrossingSize]);

  const wehaBlue = (w.primary as string) || theme.palette.primary.main;
  const appFont = theme.typography?.fontFamily || '"Open Sans", "Helvetica Neue", Arial, sans-serif';
  const paperBg =
    (typeof w.paperBackground === 'string' ? w.paperBackground : null) || theme.palette.background.paper;

  const handleConfirm = () => {
    onSelect(localSelection);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius,
          padding: 0,
          overflow: 'hidden',
          zIndex: 1000,
        },
      }}
    >
      <DialogContent style={{ padding: 0 }}>
        {fontUrl && <style dangerouslySetInnerHTML={{ __html: crossingSizeModalFontCss(fontUrl) }} />}
        <Box
          component="div"
          style={{
            margin: 0,
            padding: isMobile ? '12px 16px' : '16px 24px',
            backgroundColor: wehaBlue,
            fontFamily: appFont,
            fontSize: '1rem',
            fontWeight: 600,
            position: 'relative',
            width: '100%',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          <span style={{ color: '#fff', fontFamily: appFont }}>
            {t('weha.globalSettings.supportCrossModalTitle')}
          </span>
          <IconButton
            aria-label={t('weha.globalSettings.supportCrossModalClose')}
            onClick={onClose}
            size="small"
            style={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
          >
            ×
          </IconButton>
        </Box>
        <Box style={{ padding: isMobile ? 16 : 24 }} display="flex" flexDirection="column">
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            style={{ gap: isMobile ? 16 : 24 }}
            alignItems={isMobile ? 'center' : 'stretch'}
          >
            <Box
              flex="0 0 auto"
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                minWidth: isMobile ? 120 : 200,
                minHeight: isMobile ? 120 : 200,
                width: isMobile ? 120 : undefined,
                height: isMobile ? 120 : undefined,
                borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius,
                backgroundColor: paperBg,
              }}
            >
              <Typography
                component="span"
                style={{
                  fontFamily: fontUrl ? `'${MODAL_FONT_FAMILY}', sans-serif` : 'sans-serif',
                  fontSize: isMobile ? 80 : 240,
                  color: theme.palette.text.primary,
                }}
              >
                {supportCrossCharacter}
              </Typography>
            </Box>
            <Box flex="1 1 auto" display="flex" flexDirection="column" style={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                style={{
                  color: (w.textMuted as string) || theme.palette.text.secondary,
                  marginBottom: 8,
                }}
              >
                {t('weha.globalSettings.supportCrossModalHint')}
              </Typography>
              <RadioGroup
                value={
                  localSelection == null
                    ? 'none'
                    : crossingSizes.find(
                          (s) => s.height === localSelection.height && s.width === localSelection.width
                        )
                      ? `h${localSelection.height}-w${localSelection.width}`
                      : 'none'
                }
                onChange={(_, value) => {
                  if (value === 'none') {
                    setLocalSelection(null);
                    return;
                  }
                  const match = value.match(/^h(\d+)-w(\d+)$/);
                  if (match) {
                    const height = Number(match[1]);
                    const width = Number(match[2]);
                    const size = crossingSizes.find((s) => s.height === height && s.width === width);
                    if (size) setLocalSelection({ height: size.height, width: size.width });
                  }
                }}
              >
                <FormControlLabel
                  value="none"
                  control={<Radio color="primary" />}
                  label={
                    <Typography
                      component="span"
                      style={{
                        fontWeight: localSelection == null ? 700 : 400,
                      }}
                    >
                      {t('weha.globalSettings.supportCrossNone')}
                    </Typography>
                  }
                />
                {crossingSizes.map((size) => {
                  const value = `h${size.height}-w${size.width}`;
                  const isSelected = crossingSizeEquals(localSelection, size);
                  return (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio color="primary" />}
                      label={
                        <Typography
                          component="span"
                          style={{
                            fontWeight: isSelected ? 700 : 400,
                          }}
                        >
                          {formatCrossingSizeLabel(size)}
                        </Typography>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection={isMobile ? 'column-reverse' : 'row'}
            justifyContent="flex-end"
            style={{ marginTop: 16, gap: 8 }}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              style={{
                borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius,
              }}
            >
              {t('weha.globalSettings.supportCrossModalCancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              style={{ borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius }}
            >
              {t('weha.globalSettings.supportCrossModalConfirm')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
